import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of, from } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { map, switchMap, catchError, take } from 'rxjs/operators';
import {
  Capability,
  capabilitiesOf,
  getUserGroupsOrNull,
  resolveHome
} from '../shared/utils/auth-groups.util';

/**
 * Garde appliquée à toutes les routes du layout.
 *
 * Elle contrôle l'appartenance aux groupes Cognito, mais aussi la **cible** :
 * chaque route déclare la capacité qu'elle exige (`data.requires`), et un
 * utilisateur qui ne la porte pas est renvoyé chez lui.
 *
 * Ce test remplace celui du préfixe d'URL (« cette URL est-elle sous
 * `/manager/` ? »), qui supposait des espaces disjoints. Depuis que le
 * gestionnaire exploite le parc et que tout utilisateur FINANCE porte aussi
 * MANAGER, les deux surfaces se recouvrent : la question n'a plus de réponse
 * utile, et c'est la capacité qui décide.
 * Voir knowledge/future-features/bascule_operationnelle_vers_le_gestionnaire.md.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  private authService = inject(AuthService);
  private router = inject(Router);

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.authService.isAuthenticated$.pipe(
      take(1),
      switchMap((isAuthenticated) =>
        from(getUserGroupsOrNull()).pipe(
          map((groups) => this.resolveAccess(groups, requiredCapability(state), isAuthenticated)),
          catchError(() => of(this.router.createUrlTree(['/login'])))
        )
      )
    );
  }

  private resolveAccess(
    groups: string[] | null,
    required: Capability | null,
    isAuthenticated: boolean
  ): boolean | UrlTree {
    if (!groups) {
      return this.router.createUrlTree(['/login']);
    }

    // La session est valide mais le service ne le sait pas encore (premier
    // chargement, retour de redirection SAML) : on le remet à jour.
    if (!isAuthenticated) {
      this.authService.checkAuthStatus();
    }

    const capabilities = capabilitiesOf(groups);

    // Aucune capacité : on ne bloque pas, on renvoie chez lui — l'onboarding
    // s'il n'a aucun groupe, `/no-access` s'il ne porte que le rôle de base. Le
    // distinguer importe : l'onboarding lui dirait que son compte n'est pas
    // configuré, alors qu'il est actif et qu'il lui manque un rôle.
    if (capabilities.size === 0) {
      return this.router.createUrlTree([resolveHome(groups)]);
    }

    // Route sans exigence (le layout lui-même, le tableau de bord) : toute
    // personne à qui un espace est ouvert y a droit.
    if (required === null) {
      return true;
    }

    return capabilities.has(required)
      ? true
      : this.router.createUrlTree([resolveHome(groups)]);
  }
}

/**
 * La capacité exigée par la route demandée : la plus PROFONDE déclarée le long
 * de la branche, pour qu'un enfant puisse resserrer ce que son parent ouvre
 * (`/owners` exige OWNER_MANAGE, son onglet des comptes de gestion exige
 * TRANSACTION_VIEW).
 *
 * Lit `routeConfig.data` et non `snapshot.data` : ce dernier hérite du parent
 * selon `paramsInheritanceStrategy`, ce qui rendrait « la plus profonde »
 * ambigu — un enfant sans exigence propre porterait celle de son parent et on
 * ne pourrait plus les distinguer.
 *
 * La garde est posée sur la route PARENTE, dont le propre snapshot ne dit rien
 * de la page demandée ; c'est `RouterStateSnapshot`, l'état FUTUR déjà construit
 * quand les gardes s'exécutent, qui porte la branche complète.
 */
export function requiredCapability(state: RouterStateSnapshot): Capability | null {
  let snapshot: ActivatedRouteSnapshot | null = state.root;
  let required: Capability | null = null;

  while (snapshot) {
    const declared = snapshot.routeConfig?.data?.['requires'] as Capability | undefined;
    if (declared) {
      required = declared;
    }
    snapshot = snapshot.firstChild;
  }

  return required;
}
